import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const ActivityContext = createContext();

export function ActivityProvider({ children }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      
      // Récupérer d'abord les activités
      const { data: activities, error: activitiesError } = await supabase
        .from('activities')
        .select('*')
        .order('name');

      if (activitiesError) throw activitiesError;

      // Récupérer le nombre de pointages pour chaque activité
      const { data: pointageCounts, error: pointageError } = await supabase
        .rpc('count_pointages_by_activity');

      if (pointageError) throw pointageError;

      // Créer un dictionnaire des nombres de pointages
      const pointageCountMap = pointageCounts.reduce((acc, item) => {
        acc[item.activity_id] = item.pointage_count;
        return acc;
      }, {});

      // Ajouter le nombre de pointages à chaque activité
      const activitiesWithCount = activities.map(activity => ({
        ...activity,
        pointage_count: pointageCountMap[activity.id] || 0
      }));
      
      setActivities(activitiesWithCount);
    } catch (err) {
      console.error('Erreur lors de la récupération des activités:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const createActivity = useCallback(async (activityData) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('activities')
        .insert(activityData)
        .select();

      if (error) throw error;
      
      // Ajouter le nombre de pointages (0 pour une nouvelle activité)
      const activityWithCount = {
        ...data[0],
        pointage_count: 0
      };

      // Mettre à jour la liste des activités
      setActivities(prev => [...prev, activityWithCount]);
      return activityWithCount;
    } catch (err) {
      console.error('Erreur lors de la création de l\'activité:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateActivity = useCallback(async (id, updates) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('activities')
        .update(updates)
        .eq('id', id)
        .select();

      if (error) throw error;

      // Récupérer le nombre de pointages pour cette activité
      const { data: pointageCounts, error: pointageError } = await supabase
        .rpc('count_pointages_by_activity')
        .eq('activity_id', id);

      if (pointageError) throw pointageError;

      // Ajouter le nombre de pointages à l'activité mise à jour
      const updatedActivity = {
        ...data[0],
        pointage_count: pointageCounts[0]?.pointage_count || 0
      };
      
      // Mettre à jour la liste des activités
      setActivities(prev => 
        prev.map(activity => 
          activity.id === id ? updatedActivity : activity
        )
      );
      return updatedActivity;
    } catch (err) {
      console.error('Erreur lors de la mise à jour de l\'activité:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteActivity = useCallback(async (id) => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('activities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      // Mettre à jour la liste des activités
      setActivities(prev => prev.filter(activity => activity.id !== id));
    } catch (err) {
      console.error('Erreur lors de la suppression de l\'activité:', err);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <ActivityContext.Provider value={{ 
      activities, 
      loading, 
      error, 
      fetchActivities,
      createActivity,
      updateActivity,
      deleteActivity 
    }}>
      {children}
    </ActivityContext.Provider>
  );
}

export function useActivities() {
  const context = useContext(ActivityContext);
  if (context === undefined) {
    throw new Error('useActivities must be used within an ActivityProvider');
  }
  return context;
}
